/**
 * Web component: Product card (product-card)
 * -----------------------------------------------------------------------------
 * Product card functionality.
 * - Colour swatch update.
 * - Quick add drawer.
 *
 */

/**
 * Create new <product-card> custom element.
 */
class ProductCard extends HTMLElement {
  constructor() {
    super()

    this.mediaQuery = window.matchMedia('(max-width: 749px)')
    this.isMobile = this.mediaQuery.matches  
  }

  connectedCallback() {
    /**
     * Quick add.
     */
    this.quickAddButton = this.querySelector('.card-product__quick-add-button')
    this.productVariantsModal = this.querySelector('.product-card__variants')
    this.quickAddToCartModal = this.querySelector('quick-add-to-cart-modal')
    this.productCardInner = this.querySelector('.card__inner')
    this.productCardMedia = this.querySelector('.card__media')
    this.drawerBottom = this.querySelector('drawer-bottom')
    
    /**
     * Colour swatches.
     */
    this.swatches = [...this.querySelectorAll('[js-product-card="swatch"]')]

    /**
     * Quick add.
     */
    this.mediaQuery.addEventListener('change', this.handleMediaQueryChange.bind(this))
    this.quickAddButton?.addEventListener('click', this.handleQuickAddButtonClick.bind(this))
    this.addEventListener('mouseleave', this.handleMouseLeave.bind(this))

    /**
     * Colour swatches.
     */
    this.swatches.forEach((element) => {
      element.addEventListener('click', this.handleSwatchClick.bind(this))
    })
  }

  disconnectedCallback() {
    this.mediaQuery.removeEventListener('change', this.handleMediaQueryChange.bind(this))
    this.quickAddButton?.removeEventListener('click', this.handleQuickAddButtonClick.bind(this))
    this.removeEventListener('mouseleave', this.handleMouseLeave.bind(this))

    this.swatches.forEach((element) => {
      element.removeEventListener('click', this.handleSwatchClick.bind(this))
    })
  }

  /**
   * Handle media query change.
   * @param {Object} event - Change event.
   */
  handleMediaQueryChange(event) {
    this.isMobile = event.matches

    if (!this.isMobile) {
      this.drawerBottom?.removeAttribute('open')
    }
  }

  /**
   * Handle quick add button click.
   * @param {Object} event - Click event.
   */
  handleQuickAddButtonClick(event) {
    event.preventDefault()

    if (this.isMobile) {
      this.productCardInner?.classList.remove('card__inner-hover', 'gradient')
      setTimeout(() => {
        // Dispatch a custom event for custom functionality at search modal
        const searchDetailsOpen = document.querySelector(".header__search details")?.getAttribute('open') === '' || document.querySelector(".header__search details")?.getAttribute('open') === 'true';
        const predictiveSearchOpen = window.templateData.name === "search" &&
        document.querySelector(".template-search__search predictive-search")?.getAttribute('open') === '' || 
        document.querySelector(".template-search__search predictive-search")?.getAttribute('open') === 'true';
        if (searchDetailsOpen || predictiveSearchOpen) {
          const modalOpenEvent = new CustomEvent('modal:opened');
          document.dispatchEvent(modalOpenEvent);
        }
        this.drawerBottom?.setAttribute('open', '')
      }, 500)

      return
    }

    this.productCardInner?.classList.remove('card__inner-hover')
    this.productVariantsModal?.classList.add('open')
    this.productCardMedia?.classList.add('overlay')
  }

  /**
   * Handle mouse leave.
   */
  handleMouseLeave() {
    if (this.isMobile) {
      setTimeout(() => {
        this.productCardInner?.classList.toggle('card__inner-hover', true)
      }, 500)

      return
    }

    this.productCardMedia?.classList.remove('overlay')
    this.productCardInner?.classList.toggle('card__inner-hover', true)
    this.productVariantsModal?.classList.remove('open')
  }

  /**
   * Handle swatch click.
   */
  async handleSwatchClick(event) {
    event.preventDefault();
    try {
      const target = event.target
      const handle = target.dataset.handle
      this.classList.add('is-loading')

      /**
       * Load new template.
       */
      let response = await fetch(`/products/${handle}?view=card`)
      response = await response.text()

      /**
       * Parse new template.
       */
      const parser = new DOMParser()
      const template = parser.parseFromString(response, 'text/html')
      const productCard = template.querySelector('product-card')

      if (!productCard) {
        return
      }

      /**
       * Replace product card.
       */
      this.insertAdjacentElement('beforebegin', productCard)
      this.remove()

    } catch (error) {
      console.error('Failed to load swatch', error)
    }
  }
}

customElements.define('product-card', ProductCard)
